import axios from 'App/axios';
import * as FingerprintClient from 'App/fingerprintClient';
import { AxiosError } from 'axios';
import camelcase from 'camelcase';
import { StoreStatusReason } from 'commons/enums';
import {
  InstallationResponse,
  KycFlagType,
  MoneyObjectInterface,
  OwnerType,
} from 'commons/types';
import { TransferErrorType } from 'domains/Transfer/v1/TransferErrorType';
import { WithdrawDto } from '../withdrawsService/dtos/withdraw.dto';

export enum Permissions {
  withdrawV2 = 'withdrawal_v2',
}
export interface GetBalanceResponse {
  available: MoneyObjectInterface;
  waitingFunds: MoneyObjectInterface;
  blocked: MoneyObjectInterface;
  availableToWithdraw: MoneyObjectInterface;
  inTransit: MoneyObjectInterface;
  hasPendingTransfers?: boolean;
  transferFee: MoneyObjectInterface;
}

type GetBalanceType = (
  fetchHasPendingTransfers?: boolean,
) => Promise<GetBalanceResponse>;

const getBalance: GetBalanceType = async (fetchHasPendingTransfers = false) => {
  const response = await axios.get(
    `/admin/balances?fetchHasPendingTransfers=${fetchHasPendingTransfers}`,
  );
  return response.data;
};

type GetBalanceByFapIdType = (
  fapId: number,
  fetchHasPendingTransfers?: boolean,
) => Promise<GetBalanceResponse>;

const getBalanceByFapId: GetBalanceByFapIdType = async (
  fapId,
  fetchHasPendingTransfers = false,
) => {
  const response = await axios.get(
    `/admin/balances/ownership/${fapId}?fetchHasPendingTransfers=${fetchHasPendingTransfers}`,
  );
  return response.data;
};

export type TransferType = (
  amount?: MoneyObjectInterface,
  antifraudMetadata?: Record<string, any>,
) => Promise<WithdrawDto>;
export type TransferErrorResponseObjectType = {
  response: {
    data: {
      failureCode: TransferErrorType;
    };
  };
};

const transfer: TransferType = async (
  amount?: MoneyObjectInterface,
  antifraudMetadata?: Record<string, any>,
) => {
  const headers = await FingerprintClient.getHeaders();
  const response = await axios.post(
    '/admin/transfer',
    {
      amount,
      antifraudMetadata,
    },
    { headers },
  );
  return response.data as WithdrawDto;
};

export interface GetActiveStoreResponse {
  storeId: number;
  active: boolean;
  owner: OwnerType;
  flags: string[];
  kycFlags: KycFlagType[];
  statusReason?: StoreStatusReason;
  tiendanubeUrl?: string;
}

type GetActiveStoreType = () => Promise<GetActiveStoreResponse>;

export class StoreNotFoundError extends Error {}

const getActiveStore: GetActiveStoreType = async () => {
  try {
    const response = await axios.get('/admin/active-store');
    return response.data;
  } catch (error: unknown) {
    if ((error as AxiosError).response?.status === 404) {
      throw new StoreNotFoundError('Store not found', { cause: error });
    } else {
      throw error;
    }
  }
};

export interface GetActiveUserResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isOwner: boolean;
  isEmployee: boolean;
  roles: string[];
}

interface GetPermission {
  permitted: boolean;
}

type GetActiveUserType = () => Promise<GetActiveUserResponse | undefined>;

const getActiveUser: GetActiveUserType = async () => {
  try {
    const { data } = await axios.get('/admin/active-user');
    return (
      typeof data === 'object' &&
      Object.keys(data).reduce(
        (obj: any, key) => ({
          ...obj,
          [camelcase(key)]: data[key],
        }),
        {},
      )
    );
  } catch (error: any) {
    // eslint-disable-next-line
    console.error('Error fetching active user', error);
    return undefined;
  }
};

type GetPermissionType = (permission: string) => Promise<GetPermission>;

const getPermission: GetPermissionType = async (permission: string) => {
  const { data } = await axios.get(
    `/admin/merchants/has-permission/${permission}`,
  );

  return data;
};

type RetrieveTransferType = (withdrawId: number) => Promise<WithdrawDto>;

const retrieveTransfer: RetrieveTransferType = async (withdrawId: number) => {
  const response = await axios.get(`/admin/transfers/${withdrawId}`, {
    timeout: 5_000,
  });
  return response.data;
};

type GetInstallationType = () => Promise<InstallationResponse>;

const getInstallation: GetInstallationType = async () => {
  const { data } = await axios.get('/admin/installation');
  return data;
};

const syncKycFlags = async (): Promise<KycFlagType[]> => {
  const { data } = await axios.post<{ kycFlags: KycFlagType[] }>(
    '/admin/kyc-flags/sync',
  );
  return data.kycFlags;
};

interface MerchantServiceInterface {
  getBalance: GetBalanceType;
  getBalanceByFapId: GetBalanceByFapIdType;
  transfer: TransferType;
  getActiveStore: GetActiveStoreType;
  getActiveUser: GetActiveUserType;
  getPermission: GetPermissionType;
  retrieveTransfer: RetrieveTransferType;
  getInstallation: GetInstallationType;
  syncKycFlags: () => Promise<KycFlagType[]>;
}

const merchantService: MerchantServiceInterface = {
  getBalance,
  getBalanceByFapId,
  transfer,
  getActiveStore,
  getActiveUser,
  getPermission,
  retrieveTransfer,
  getInstallation,
  syncKycFlags,
};

export default merchantService;
